// Tier catalog mirrors backend (server.py SPONSOR_TIERS) for UI lookup.
export const TIERS = [
  { id: "title",      label: "Presented By",      sublabel: "Title Partner",      value_cr: 3.0,   slots: 1,  kind: "sponsor",    short: "Title" },
  { id: "powered",    label: "Powered By",        sublabel: "Headline Sponsor",   value_cr: 2.0,   slots: 2,  kind: "sponsor",    short: "Powered" },
  { id: "co_powered", label: "Co-Powered By",     sublabel: "Major Sponsor",      value_cr: 1.0,   slots: 3,  kind: "sponsor",    short: "Co-Powered" },
  { id: "associate",  label: "Associate Partner", sublabel: "Supporting Sponsor", value_cr: 0.5,   slots: 4,  kind: "sponsor",    short: "Associate" },
  // Activation / Exhibition Spaces — v3 (Dec 2026 catalog)
  { id: "holy_grail", label: "Holy Grail Only",   sublabel: "15×10m · 150 sqm",   value_cr: 0.35,  slots: 1,  kind: "activation", short: "Holy Grail" },
  { id: "grail",      label: "Grail Only",        sublabel: "10×10m · 100 sqm",   value_cr: 0.20,  slots: 2,  kind: "activation", short: "Grail" },
  { id: "deadstock",  label: "Deadstock Only",    sublabel: "8×8m · 64 sqm",      value_cr: 0.15,  slots: 6,  kind: "activation", short: "Deadstock" },
  { id: "heat",       label: "Heat Only",         sublabel: "6×6m · 36 sqm",      value_cr: 0.10,  slots: 8,  kind: "activation", short: "Heat" },
  { id: "hype",       label: "Hype Only",         sublabel: "6×4m · 24 sqm",      value_cr: 0.075, slots: 12, kind: "activation", short: "Hype" },
  { id: "lace_up",    label: "Lace Up Only",      sublabel: "4×4m · 16 sqm",      value_cr: 0.05,  slots: 20, kind: "activation", short: "Lace Up" },
];

export const TIER_BY_ID = Object.fromEntries(TIERS.map((t) => [t.id, t]));

export const SPONSOR_TIERS = TIERS.filter((t) => t.kind === "sponsor");
export const ACTIVATION_TIERS = TIERS.filter((t) => t.kind === "activation");

export const SPONSOR_RACK_CR = 12.0;       // Per spec
export const ACTIVATION_RACK_CR = 4.35;    // v3 catalog: 1×35L + 2×20L + 6×15L + 8×10L + 12×7.5L + 20×5L = 435 L
export const TOTAL_INVENTORY_CR = 16.35;   // Sponsors (12) + Activation (4.35)

export const WAVES = [
  { id: 1, label: "Wave 1", range: "Jul – Aug 2026", target_cr: 3.0 },
  { id: 2, label: "Wave 2", range: "Sep – Oct 2026" },
  { id: 3, label: "Wave 3", range: "Nov 2026" },
];

export const STATUS_OPTIONS = ["prospect", "negotiating", "closed"];
export const BACKSTAGE_STATUS = ["open", "negotiating", "sold"];

// Flea Market — flat 30 slots at ₹ 50,000 each (₹ 0.5 L per slot).
export const FLEA_TOTAL_SLOTS = 30;
export const FLEA_BASE_LAKHS = 0.5;
export const FLEA_RACK_LAKHS = FLEA_TOTAL_SLOTS * FLEA_BASE_LAKHS; // 15 L

// Returns the actual deal value in ₹ Cr for a sponsorship row.
// Uses closing_price_cr when set; otherwise falls back to rack value (tier × slots).
export function effectiveValue(row) {
  if (!row) return 0;
  const cp = row.closing_price_cr;
  if (cp != null && cp !== "" && !Number.isNaN(Number(cp))) return Number(cp);
  return (Number(row.value_cr) || 0) * (Number(row.slots_taken) || 1);
}

export function rackValue(row) {
  if (!row) return 0;
  return (Number(row.value_cr) || 0) * (Number(row.slots_taken) || 1);
}

// Payment helpers — compute totals & status from installments[]
export function paidTotal(row) {
  if (!row?.installments) return 0;
  return row.installments.reduce((s, i) => {
    if (!i.paid) return s;
    const amt = i.paid_amount_cr != null && i.paid_amount_cr !== "" ? Number(i.paid_amount_cr) : Number(i.amount_cr || 0);
    return s + (Number.isFinite(amt) ? amt : 0);
  }, 0);
}

export function billedTotal(row) {
  if (!row?.installments?.length) return effectiveValue(row);
  return row.installments.reduce((s, i) => s + (Number(i.amount_cr) || 0), 0);
}

// Returns 'paid' | 'partial' | 'unbilled' | 'overdue'
export function paymentStatus(row) {
  if (!row || row.status !== "closed") return "n/a";
  const effective = effectiveValue(row);
  if (effective <= 0) return "n/a";
  const installments = row.installments || [];
  const paid = paidTotal(row);
  const today = new Date().toISOString().slice(0, 10);

  // No installments configured yet → unbilled (track 30-day rule on signed_at)
  if (installments.length === 0) {
    if (row.signed_at && row.signed_at < new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)) {
      return "overdue";
    }
    return "unbilled";
  }

  // Has any installment with due_date < today and unpaid → overdue
  const hasOverdue = installments.some((i) => !i.paid && i.due_date && i.due_date < today);
  if (hasOverdue) return "overdue";

  if (paid >= effective - 0.001) return "paid";
  if (paid > 0) return "partial";
  return "unbilled";
}

export const EVENT_DATE = new Date("2026-12-19T00:00:00+05:30");

// Deliverables matrix. Each row: { name, values: [10 tier columns] }, value = 'yes' | 'neg' | 'no'
// Column order matches TIERS array:
// [title, powered, co_powered, associate, holy_grail, grail, deadstock, heat, hype, lace_up]
const Y = "yes", N = "no", M = "neg";

export const DELIVERABLES = [
  {
    section: "Pre-Event",
    range: "Sep – Dec 2026",
    rows: [
      { name: 'Event name integration "ISF 2026 Presented by [Brand]"', values: [Y, N, N, N, N, N, N, N, N, N] },
      { name: "Powered By credit line",                                 values: [N, Y, M, M, N, N, N, N, N, N] },
      { name: "Logo on all event collateral",                           values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Dedicated brand announcement post / Reel (ISF IG 40k+)", values: [Y, Y, M, M, M, M, M, M, N, N] },
      { name: "Brand logo in all social posts",                         values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Co-branded stories & countdowns",                        values: [Y, Y, M, M, Y, N, N, N, N, N] },
      { name: "Creator seeding campaign",                               values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "Branded ticketing page integration (BookMyShow)",        values: [Y, Y, N, N, N, N, N, N, N, N] },
      { name: "Email newsletter / CRM mention",                         values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Website logo placement",                                 values: [Y, Y, Y, Y, M, M, M, N, N, N] },
      { name: "Featured in paid media / ads (Meta, YouTube, OOH)",      values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "PR & press release mentions",                            values: [Y, Y, Y, M, N, N, N, N, N, N] },
    ],
  },
  {
    section: "During Event",
    range: "19 – 20 Dec 2026",
    rows: [
      { name: 'Main stage naming rights "[Brand] Stage at ISF"',         values: [Y, N, N, N, N, N, N, N, N, N] },
      { name: "Entry gate / facade branding",                            values: [Y, Y, N, N, N, N, N, N, N, N] },
      { name: "Premium logo on all venue signage",                       values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Brand on event wristbands (30,000)",                      values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "Brand on event merchandise / tote bags",                  values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "Dedicated brand experience zone / booth",                 values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
      { name: "Premium / high-footfall zone placement",                  values: [Y, Y, Y, M, Y, Y, Y, N, N, N] },
      { name: "Main stage branding LED & backdrop",                      values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "MC / host brand shoutouts on stage",                      values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Artist co-branding opportunity",                          values: [Y, M, M, N, N, N, N, N, N, N] },
      { name: "Product sampling / giveaways",                            values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
      { name: "Exclusive product drop / launch rights",                  values: [Y, Y, M, N, Y, Y, Y, M, M, N] },
      { name: "Interactive gamification / contest",                      values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
      { name: "Branded content studio / shoot zone",                     values: [Y, Y, Y, M, Y, Y, Y, M, M, N] },
      { name: "Live commerce / on-site direct sales (1L+ transactions)", values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
      { name: "Dedicated creator meet & greet zone",                     values: [Y, Y, M, N, M, N, N, N, N, N] },
      { name: "Creator live content at brand booth",                     values: [Y, Y, Y, Y, Y, Y, Y, Y, M, M] },
      { name: "VIP / VVIP lounge branding",                              values: [Y, Y, N, N, N, N, N, N, N, N] },
      { name: "Complimentary passes for brand team",                     values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
    ],
  },
  {
    section: "Post-Event",
    range: "Jan 2027 onwards",
    rows: [
      { name: "Brand in official after-movie (50M+ views potential)",    values: [Y, Y, M, N, M, N, N, N, N, N] },
      { name: "Branded recap reels / carousels",                         values: [Y, Y, Y, M, M, M, M, N, N, N] },
      { name: "UGC rights & asset library (5,000+ moments)",             values: [Y, Y, M, N, M, N, N, N, N, N] },
      { name: "Creator post-event content series",                       values: [Y, Y, Y, M, N, N, N, N, N, N] },
      { name: "Co-branded content asset pack (500+ pieces)",             values: [Y, Y, N, N, N, N, N, N, N, N] },
      { name: "Post-event ROI report",                                   values: [Y, Y, Y, Y, Y, Y, Y, Y, Y, Y] },
      { name: "Brand in press coverage recap",                           values: [Y, Y, M, N, N, N, N, N, N, N] },
      { name: "Right of first refusal — 2027 edition",                   values: [Y, Y, M, N, M, M, N, N, N, N] },
    ],
  },
];
